import { useState, useEffect, useRef, useCallback } from 'react';
import { pushAPI } from '../services/api';

/**
 * useNotifications - Manages both local notifications and Web Push subscriptions
 *
 * - Shows local notification after inactivity
 * - Subscribes to Web Push for server-sent notifications
 */
export function useNotifications(messages, user = null) {
  const [permission, setPermission] = useState('default');
  const [isSupported, setIsSupported] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [inAppNotification, setInAppNotification] = useState(null); // For in-app toast
  const inactivityTimerRef = useRef(null);
  const lastNotificationTimeRef = useRef(0);
  const retryTimerRef = useRef(null);
  const retryCountRef = useRef(0);
  const subscribeToPushRef = useRef(null);

  // Schedule retry with exponential backoff
  const scheduleRetry = useCallback(() => {
    // Clear any existing retry timer
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
    }

    // Max 5 retries
    if (retryCountRef.current >= 5) {
      console.log('[Push] Max retries reached, giving up');
      return;
    }

    // Exponential backoff: 2s, 4s, 8s, 16s, 32s
    const delay = Math.pow(2, retryCountRef.current) * 2000;
    retryCountRef.current++;

    console.log(`[Push] Scheduling retry #${retryCountRef.current} in ${delay / 1000}s`);
    retryTimerRef.current = setTimeout(() => {
      console.log(`[Push] Executing retry #${retryCountRef.current}`);
      subscribeToPushRef.current?.();
    }, delay);
  }, []);

  // Subscribe to Web Push notifications
  const subscribeToPush = useCallback(async () => {
    try {
      console.log('[Push] Starting subscription process...');

      // Check if service worker is supported
      if (!('serviceWorker' in navigator)) {
        console.warn('[Push] Service Worker not supported');
        return false;
      }

      if (!('PushManager' in window)) {
        console.warn('[Push] Push API not supported');
        return false;
      }

      // Wait for service worker to be ready
      const registration = await navigator.serviceWorker.ready;
      console.log('[Push] Service Worker ready');

      // Check if already subscribed
      const existingSubscription = await registration.pushManager.getSubscription();
      if (existingSubscription) {
        console.log('[Push] Found existing subscription:', existingSubscription.endpoint.substring(0, 50) + '...');

        // Always sync to server to ensure server has the subscription
        try {
          console.log('[Push] Syncing existing subscription to server...');
          await pushAPI.subscribe(existingSubscription.toJSON());
          console.log('[Push] ✅ Existing subscription synced to server');
          setIsSubscribed(true);
          return true;
        } catch (error) {
          console.error('[Push] ❌ Failed to sync existing subscription:', error);
          // If sync fails, try to unsubscribe and create new subscription below
          console.log('[Push] Attempting to unsubscribe and create new subscription...');
          await existingSubscription.unsubscribe();
          // Fall through to create new subscription
        }
      }

      // Get VAPID public key from server
      console.log('[Push] Fetching VAPID public key...');
      const { publicKey } = await pushAPI.getVapidPublicKey();
      console.log('[Push] VAPID public key received');

      // Convert base64 string to Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
          .replace(/-/g, '+')
          .replace(/_/g, '/');

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      const applicationServerKey = urlBase64ToUint8Array(publicKey);

      // Subscribe to push notifications
      console.log('[Push] Subscribing to push notifications...');
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey
      });

      console.log('[Push] Push subscription created:', subscription.endpoint.substring(0, 50) + '...');

      // Send subscription to server
      console.log('[Push] Sending subscription to server...');
      await pushAPI.subscribe(subscription.toJSON());
      console.log('[Push] ✅ Subscription saved to server');

      setIsSubscribed(true);
      retryCountRef.current = 0; // Reset retry count on success
      return true;
    } catch (error) {
      console.error('[Push] ❌ Subscription failed:', error);
      scheduleRetry(); // Schedule retry with exponential backoff
      return false;
    }
  }, [scheduleRetry]);

  // Keep ref up to date with latest subscribeToPush function
  useEffect(() => {
    subscribeToPushRef.current = subscribeToPush;
  }, [subscribeToPush]);

  // Check if notifications are supported and auto-subscribe if permission already granted
  useEffect(() => {
    if ('Notification' in window) {
      setIsSupported(true);
      const currentPermission = Notification.permission;
      setPermission(currentPermission);
      console.log('[Notifications] Supported:', true, 'Permission:', currentPermission);

      // Only auto-subscribe if user is authenticated AND permission already granted
      if (currentPermission === 'granted' && user) {
        console.log('[Notifications] Permission already granted and user authenticated - auto-subscribing to push...');
        subscribeToPush();
      } else if (currentPermission === 'granted' && !user) {
        console.log('[Notifications] Permission granted but user not authenticated yet - waiting...');
      }
    } else {
      console.warn('[Notifications] NOT SUPPORTED in this browser');
    }
  }, [subscribeToPush, user]);

  // Periodic check to keep subscription in sync (every 5 minutes)
  useEffect(() => {
    if (permission !== 'granted' || !isSubscribed) {
      return;
    }

    console.log('[Push] Starting periodic sync check (every 5 minutes)');

    const syncInterval = setInterval(() => {
      console.log('[Push] Running periodic sync check...');
      subscribeToPush();
    }, 5 * 60 * 1000); // 5 minutes

    return () => {
      console.log('[Push] Stopping periodic sync check');
      clearInterval(syncInterval);
    };
  }, [permission, isSubscribed, subscribeToPush]);

  // Show banner after user has interacted with the app
  useEffect(() => {
    console.log('[Notifications] Banner check:', {
      isSupported,
      permission,
      messagesCount: messages.length,
      dismissed: localStorage.getItem('notification-banner-dismissed')
    });

    if (!isSupported) {
      console.log('[Notifications] Banner blocked - not supported');
      return;
    }

    if (permission !== 'default') {
      console.log('[Notifications] Banner blocked - permission is:', permission, '(need "default")');
      return;
    }

    // Check if banner was dismissed
    const dismissed = localStorage.getItem('notification-banner-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
      const timeAgo = Date.now() - dismissedTime;
      if (timeAgo < sevenDaysInMs) {
        console.log('[Notifications] Banner blocked - dismissed', Math.floor(timeAgo / 1000 / 60 / 60), 'hours ago');
        return;
      }
    }

    // Show banner after user has sent at least 1 message
    if (messages.length > 0) {
      console.log('[Notifications] ✅ All checks passed! Scheduling banner to show in 5 seconds...');
      // Wait 5 seconds before showing banner
      const timer = setTimeout(() => {
        console.log('[Notifications] 🔔 Showing banner NOW!');
        setShowBanner(true);
      }, 5000);

      return () => clearTimeout(timer);
    } else {
      console.log('[Notifications] Banner blocked - no messages yet');
    }
  }, [isSupported, permission, messages.length]);

  // Request notification permission
  const requestPermission = async () => {
    if (!isSupported) {
      console.warn('[Notifications] Not supported in this browser');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      setShowBanner(false); // Hide banner after permission is granted/denied

      // If permission granted, subscribe to Web Push
      if (result === 'granted') {
        console.log('[Notifications] Permission granted - subscribing to push...');
        await subscribeToPush();
      }

      return result === 'granted';
    } catch (error) {
      console.error('[Notifications] Permission request failed:', error);
      return false;
    }
  };

  // Dismiss the banner
  const dismissBanner = () => {
    setShowBanner(false);
    localStorage.setItem('notification-banner-dismissed', Date.now().toString());
  };

  // Show a notification using Service Worker (works in PWAs when backgrounded)
  const showNotification = useCallback(async (title, options = {}) => {
    console.log('[Notifications] showNotification called with:', { title, options, isSupported, permission });

    // Check if page has focus
    const hasFocus = document.hasFocus();
    const isVisible = document.visibilityState === 'visible';
    console.log('[Notifications] Document state:', { hasFocus, isVisible });

    if (!isSupported || permission !== 'granted') {
      console.warn('[Notifications] Cannot show - isSupported:', isSupported, 'permission:', permission);
      return null;
    }

    // If page is focused/visible, show in-app notification instead
    if (hasFocus || isVisible) {
      console.log('[Notifications] 📱 Page is focused - showing in-app notification');
      setInAppNotification({
        title,
        body: options.body || '',
        ...options
      });
      return true;
    }

    // Page is in background - use system notification
    try {
      console.log('[Notifications] 🔔 Page is backgrounded - showing system notification');
      const registration = await navigator.serviceWorker.ready;
      console.log('[Notifications] Service Worker ready:', registration);

      const iconUrl = `${window.location.origin}/icon-192x192.png`;

      await registration.showNotification(title, {
        icon: iconUrl,
        badge: iconUrl,
        vibrate: [200, 100, 200],
        requireInteraction: false,
        ...options
      });

      console.log('[Notifications] ✅ System notification sent!');
      return true;
    } catch (error) {
      console.error('[Notifications] ❌ Failed to create notification:', error);
      return null;
    }
  }, [isSupported, permission]);

  // Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  // Start inactivity timer
  const startInactivityTimer = useCallback((lastAssistantMessage) => {
    resetInactivityTimer();

    // Don't start timer if no message or permission not granted
    if (!lastAssistantMessage || permission !== 'granted') {
      return;
    }

    // Don't spam notifications - only one per 5 minutes
    const timeSinceLastNotification = Date.now() - lastNotificationTimeRef.current;
    const FIVE_MINUTES = 5 * 60 * 1000;
    if (timeSinceLastNotification < FIVE_MINUTES) {
      console.log('[Notifications] Skipping - too soon since last notification');
      return;
    }

    console.log('[Notifications] Starting 60-second inactivity timer (TESTING)');

    inactivityTimerRef.current = setTimeout(() => {
      console.log('[Notifications] 60 seconds passed - showing notification');

      // Clean the message (remove MI technique labels and content tags)
      let cleanMessage = lastAssistantMessage
        .replace(/\{\{[^}]*\}\}/g, '') // Remove {{MI labels}}
        .replace(/<[^>]+>/g, '') // Remove content tags
        .trim();

      // Truncate if too long
      if (cleanMessage.length > 100) {
        cleanMessage = cleanMessage.substring(0, 97) + '...';
      }

      // Show notification via Service Worker
      showNotification('Still curious about your answer', {
        body: cleanMessage,
        tag: 'sophia-reminder', // Replace previous notification
        requireInteraction: false
      }).then((result) => {
        if (result) {
          console.log('[Notifications] ✅ Notification triggered successfully!');
          lastNotificationTimeRef.current = Date.now();
        } else {
          console.error('[Notifications] ❌ Failed to trigger notification');
        }
      }).catch((error) => {
        console.error('[Notifications] ❌ Notification error:', error);
      });
    }, 60 * 1000); // 60 seconds (TEMPORARY FOR TESTING - change back to 3 * 60 * 1000)
  }, [resetInactivityTimer, permission, showNotification]);

  // Track user activity and reset timer
  useEffect(() => {
    // Get last assistant message
    const lastAssistantMessage = messages
      .slice()
      .reverse()
      .find(msg => msg.role === 'assistant')?.content;

    if (!lastAssistantMessage) {
      return;
    }

    // Activity events that should reset the timer
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart'];

    const handleActivity = () => {
      // Reset and restart timer on user activity
      startInactivityTimer(lastAssistantMessage);
    };

    // Start initial timer
    startInactivityTimer(lastAssistantMessage);

    // Listen for user activity
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      resetInactivityTimer();
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [messages, permission, startInactivityTimer, resetInactivityTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      resetInactivityTimer();
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
      }
    };
  }, [resetInactivityTimer]);

  return {
    isSupported,
    permission,
    requestPermission,
    showNotification,
    showBanner,
    dismissBanner,
    isSubscribed,
    subscribeToPush,
    inAppNotification,
    clearInAppNotification: () => setInAppNotification(null)
  };
}
